/**
 * StrategicOverview — Turn AQ.
 *
 * Monthly editorial report:
 *   - Month tabs (last 12 months with data)
 *   - 5 regions × top 3 curated articles (LLM-selected + reason)
 *   - "Trends of the month" section (3 cross-region trends)
 *
 * Data: GET /api/editorial/strategic-overview/monthly?month=YYYY-MM
 *       GET /api/editorial/strategic-overview/months  (tab list)
 * On any error the parent editorial page keeps working — this component
 * renders an empty state, never throws.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Compass, Newspaper, ExternalLink, RefreshCw, TrendingUp, Sparkles,
} from "lucide-react";
import { API, authHeaders } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

// ---------- Tokens (light editorial surface, aligned with the rest of
// the Editorial page). ----------
const T = {
  navy:   "#0F1B2D",
  mut:    "#3D4B62",
  sub:    "#5B6B82",
  border: "#E5E9F0",
  soft:   "#F8FAFC",
  surface:"#FFFFFF",
  accent: "#0FA9DB",
  accentSoft:   "#E6F4FB",
  accentBorder: "#B8E1F3",
  emerald:      "#0F9E75",
  emeraldSoft:  "#E2F5EE",
};

// ---------- Format helpers ----------
function _monthLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", {
    month: "short", year: "numeric",
  });
}
function _isoDay(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB",
    { day: "numeric", month: "short" });
}
function _currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// ---------- Fetchers ----------
async function fetchMonthly(month, force = false) {
  const q = new URLSearchParams({ month });
  if (force) q.set("force", "true");
  const res = await fetch(
    `${API}/editorial/strategic-overview/monthly?${q.toString()}`,
    { headers: authHeaders(), credentials: "include" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
async function fetchMonths() {
  const res = await fetch(
    `${API}/editorial/strategic-overview/months`,
    { headers: authHeaders(), credentials: "include" },
  );
  if (!res.ok) return { months: [] };
  return res.json();
}

// ---------- Article card ----------
function ArticleRow({ a, rank }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: rank * 0.04, ease: "easeOut" }}
      data-testid={`overview-article-${a.id}`}
      className="group flex gap-4 rounded-lg p-3 transition-colors hover:bg-hm-bg-1"
      style={{ border: `1px solid transparent` }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.border; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "transparent"; }}
    >
      {/* Thumbnail (100 × 100) with graceful fallback. */}
      <div
        className="shrink-0 rounded-lg overflow-hidden grid place-items-center"
        style={{ width: 100, height: 100, background: T.accentSoft }}
      >
        {a.image_url ? (
          <>
            <img
              src={a.image_url}
              alt=""
              loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const sib = e.currentTarget.nextElementSibling;
                if (sib) sib.style.display = "grid";
              }}
            />
            <Newspaper
              className="h-6 w-6"
              style={{ color: T.accent, opacity: 0.5, display: "none" }}
              aria-hidden
            />
          </>
        ) : (
          <Newspaper
            className="h-6 w-6"
            style={{ color: T.accent, opacity: 0.5 }}
            aria-hidden
          />
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <span
            className="mono"
            style={{
              color: T.sub, fontSize: 10, letterSpacing: "0.1em",
              fontWeight: 700,
            }}
          >
            {String(rank + 1).padStart(2, "0")}
          </span>
          {a.origin === "wingman" && (
            <span
              data-testid="overview-badge-wingman"
              style={{
                color: T.emerald, background: T.emeraldSoft,
                border: `1px solid ${T.emerald}22`,
                fontSize: 10, fontWeight: 600,
                padding: "1px 6px", borderRadius: 4,
                letterSpacing: 0.4, textTransform: "uppercase",
              }}
            >
              Wingman
            </span>
          )}
        </div>
        <h4
          style={{
            color: T.navy, fontSize: 15, fontWeight: 600, lineHeight: 1.35,
            margin: 0,
          }}
        >
          {a.title}
        </h4>
        {a.summary && (
          <p
            style={{
              color: T.mut, fontSize: 12.5, lineHeight: 1.5, margin: 0,
            }}
            className="line-clamp-2"
          >
            {a.summary}
          </p>
        )}
        {a.importance_reason && (
          <p
            data-testid={`overview-reason-${a.id}`}
            style={{
              color: T.accent, fontSize: 12, lineHeight: 1.45,
              margin: 0, fontStyle: "italic",
              borderLeft: `2px solid ${T.accentBorder}`,
              paddingLeft: 8,
            }}
          >
            {a.importance_reason}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1">
          {a.source_name && (
            <span
              className="mono"
              style={{ color: T.sub, fontSize: 11, fontWeight: 500 }}
            >
              {a.source_name}
            </span>
          )}
          {a.brief_date && (
            <>
              <span style={{ color: T.border }}>·</span>
              <span
                className="mono"
                style={{ color: T.sub, fontSize: 11 }}
              >
                {_isoDay(a.brief_date)}
              </span>
            </>
          )}
          {a.source_url && (
            <a
              href={a.source_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`overview-article-link-${a.id}`}
              className="ml-auto inline-flex items-center gap-1 hover:underline"
              style={{ color: T.accent, fontSize: 11.5, fontWeight: 500 }}
            >
              <ExternalLink className="h-3 w-3" />
              Read
            </a>
          )}
        </div>
      </div>
    </motion.article>
  );
}

// ---------- Region section ----------
function RegionSection({ region, data, idx }) {
  const list = data?.top_articles || [];
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: idx * 0.05, ease: "easeOut" }}
      data-testid={`overview-region-${region}`}
      className="flex flex-col gap-4"
    >
      <div className="flex items-baseline gap-3 pb-2 border-b"
           style={{ borderColor: T.border }}>
        <h3
          style={{
            color: T.navy, fontSize: 20, fontWeight: 600,
            letterSpacing: "-0.01em", margin: 0,
          }}
        >
          {data?.label}
        </h3>
        <span
          className="mono"
          style={{
            color: T.sub, fontSize: 11, letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          {data?.article_count_total || 0} articles this month
        </span>
      </div>
      {list.length === 0 ? (
        <div
          className="rounded-lg py-6 px-4 text-center"
          style={{ background: T.soft, border: `1px dashed ${T.border}` }}
        >
          <p style={{ color: T.sub, fontSize: 12.5 }}>
            No articles this month.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {list.map((a, i) => <ArticleRow key={a.id} a={a} rank={i} />)}
        </div>
      )}
    </motion.section>
  );
}

// ---------- Trends section ----------
function TrendsSection({ trends }) {
  if (!trends || trends.length === 0) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.34, ease: "easeOut" }}
      data-testid="overview-trends"
      className="mt-12 pt-10 border-t"
      style={{ borderColor: T.border }}
    >
      <div className="flex items-center gap-2.5 mb-6">
        <TrendingUp className="h-4 w-4" style={{ color: T.accent }} />
        <h3
          className="mono"
          style={{
            color: T.accent, fontSize: 11, fontWeight: 700,
            letterSpacing: "0.16em", textTransform: "uppercase",
          }}
        >
          Trends of the month
        </h3>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {trends.map((t, i) => (
          <motion.article
            key={i}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.24, delay: i * 0.06, ease: "easeOut" }}
            data-testid={`overview-trend-${i}`}
            className="relative rounded-lg p-5 flex flex-col gap-3"
            style={{
              background: T.soft, border: `1px solid ${T.border}`,
            }}
          >
            <span
              aria-hidden
              className="mono"
              style={{
                position: "absolute", top: 12, right: 16,
                color: T.accent, opacity: 0.35,
                fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em",
                lineHeight: 1,
              }}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <div>
              <Sparkles
                className="h-3.5 w-3.5"
                style={{ color: T.accent, opacity: 0.7 }}
                aria-hidden
              />
            </div>
            <h4
              style={{
                color: T.navy, fontSize: 16, fontWeight: 600,
                lineHeight: 1.3, margin: 0, letterSpacing: "-0.01em",
                fontFamily: "'Georgia', 'Times New Roman', serif",
                fontStyle: "italic",
                paddingRight: 40,
              }}
            >
              {t.title}
            </h4>
            <p
              style={{
                color: T.mut, fontSize: 12.5, lineHeight: 1.55, margin: 0,
              }}
            >
              {t.description}
            </p>
          </motion.article>
        ))}
      </div>
    </motion.section>
  );
}

// ---------- Skeleton ----------
function OverviewSkeleton() {
  return (
    <div data-testid="overview-loading" className="flex flex-col gap-8">
      <Skeleton className="h-8 w-56" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-3">
          <Skeleton className="h-5 w-40" />
          {Array.from({ length: 2 }).map((__, j) => (
            <div key={j} className="flex gap-4">
              <Skeleton className="h-24 w-24 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------- Main ----------
export default function StrategicOverview() {
  const qc = useQueryClient();
  const [selectedMonth, setSelectedMonth] = useState(_currentMonth());

  const monthsQ = useQuery({
    queryKey: ["overview-months"],
    queryFn: fetchMonths,
    staleTime: 300_000,
    retry: false,
  });
  const availableMonths = useMemo(() => {
    const list = monthsQ.data?.months || [];
    // Keep 12 months of tabs; ensure current month is in the list even if
    // there is no data yet (so the "empty state" tab exists).
    const now = _currentMonth();
    const withNow = list.includes(now) ? list : [now, ...list];
    return withNow.slice(0, 12);
  }, [monthsQ.data]);

  // Sync selectedMonth if not in list.
  useEffect(() => {
    if (availableMonths.length === 0) return;
    if (!availableMonths.includes(selectedMonth)) {
      setSelectedMonth(availableMonths[0]);
    }
  }, [availableMonths, selectedMonth]);

  const q = useQuery({
    queryKey: ["overview-monthly", selectedMonth],
    queryFn: () => fetchMonthly(selectedMonth, false),
    staleTime: 300_000,
    retry: false,
    enabled: !!selectedMonth,
  });

  const regenerate = useMutation({
    mutationFn: () => fetchMonthly(selectedMonth, true),
    onMutate: () => toast.info("Regenerating this month's report…"),
    onSuccess: (data) => {
      qc.setQueryData(["overview-monthly", selectedMonth], data);
      toast.success("Report updated");
    },
    onError: (e) => toast.error(`Regeneration failed: ${String(e).slice(0, 100)}`),
  });

  const data = q.data;
  const regions = data?.regions || {};
  const regionOrder = ["europe", "americas", "middle_east", "oceania", "asia"];

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: "easeOut" }}
      data-testid="strategic-overview"
      className="mb-8"
      style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 16, padding: 32,
        boxShadow: "0 1px 3px rgba(15,27,45,0.03)",
      }}
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2.5">
          <Compass className="h-4 w-4" style={{ color: T.accent }} />
          <span
            className="mono"
            data-testid="overview-title"
            style={{
              color: T.navy, fontSize: 12, fontWeight: 700,
              letterSpacing: "0.14em", textTransform: "uppercase",
            }}
          >
            Strategic overview
          </span>
          <span
            className="mono"
            style={{
              color: T.sub, fontSize: 11, letterSpacing: "0.06em",
            }}
          >
            · monthly
          </span>
        </div>
        <button
          type="button"
          onClick={() => regenerate.mutate()}
          disabled={regenerate.isPending || q.isPending}
          data-testid="overview-regenerate-btn"
          className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] transition-colors"
          style={{
            color: regenerate.isPending ? T.sub : T.mut,
            borderColor: T.border,
            background: regenerate.isPending ? T.soft : T.surface,
            cursor: regenerate.isPending ? "wait" : "pointer",
          }}
          title="Regenerate this month's report"
        >
          <RefreshCw
            className={`h-3 w-3 ${regenerate.isPending ? "animate-spin" : ""}`}
          />
          Regenerate
        </button>
      </div>

      {/* Month tabs */}
      <div
        role="tablist"
        aria-label="Month"
        data-testid="overview-month-tabs"
        className="flex flex-wrap gap-1.5 mb-8"
      >
        {availableMonths.map((m) => {
          const active = m === selectedMonth;
          return (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setSelectedMonth(m)}
              data-testid={`overview-month-tab-${m}`}
              className="mono text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-md transition-colors"
              style={{
                color: active ? "#FFFFFF" : T.mut,
                background: active ? T.accent : T.soft,
                border: `1px solid ${active ? T.accent : T.border}`,
                fontWeight: active ? 700 : 500,
              }}
            >
              {_monthLabel(m)}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {q.isPending ? (
        <OverviewSkeleton />
      ) : q.isError || !data ? (
        <div
          data-testid="overview-empty"
          className="rounded-lg py-8 px-6 text-center"
          style={{ background: T.soft, border: `1px dashed ${T.border}` }}
        >
          <p style={{ color: T.sub, fontSize: 13 }}>
            No strategic overview available for this month.
          </p>
        </div>
      ) : (
        <>
          {data.error && (
            <div
              className="mb-6 rounded-md px-3 py-2 text-[12px]"
              style={{
                background: "#FEF3C7", color: "#92400E",
                border: "1px solid #FDE68A",
              }}
              data-testid="overview-warning"
            >
              Report generation temporarily unavailable — retry in a
              moment.
            </div>
          )}
          <div className="flex flex-col gap-12">
            {regionOrder.map((rk, i) => (
              <RegionSection
                key={rk}
                region={rk}
                data={regions[rk]}
                idx={i}
              />
            ))}
          </div>
          <TrendsSection trends={data.trends} />
        </>
      )}
    </motion.section>
  );
}
